import { Component, OnDestroy, OnInit } from '@angular/core';

import { Store } from '@ngrx/store';

import { Observable, Subscription } from 'rxjs';

import { SETTINGS_ROUTES } from '../../../dashboard/settings/settings.routes';
import { DASHBOARD_ROUTES } from '../../../dashboard/dashboard.routes';
import { STUDIES_ROUTES } from '../../../dashboard/studies/studies.routes';
import { AppState } from '../../../state';
import {
  selectIsLoadingSelectedStudy,
  selectSelectedStudy,
  selectSelectedStudyLoaded,
  selectSideNavType,
  selectSupportedLanguages,
} from '../../../dashboard/state/study/study.selectors';
import { StudyType } from '../../../models/study-type';
import { Study } from 'src/app/models/study';
import { User } from '../../../models/user';
import { Language } from 'src/app/models/language';
import { SubscriptionTranslation } from 'src/app/models/subscription-translation';
import { PrivilegeService } from '../../../dashboard/services/privilege/privilege.service';
import { PRIVILEGE_SECTIONS } from '../../../core/dashboard-constants';
import { SubscriptionService } from '../../../dashboard/services/subscription/subscription.service';

enum SideNavType {
  SETTINGS = 'settings',
  CREATE = 'create',
}

@Component({
  selector: 'dashboard-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
})
export class SidenavComponent implements OnInit, OnDestroy {
  loadingSelectedStudy$: Observable<boolean>;
  selectedStudyLoaded$: Observable<boolean>;
  selectedStudySubscription: Subscription;
  selectedStudy: Study;
  sidenavType$: Observable<string>;
  SideNavType = SideNavType;
  user: User;
  supportedLanguages$: Observable<Language[]>;
  supportedLanguages: Language[];
  subscriptionTranslations: SubscriptionTranslation[];
  StudyType = StudyType;
  private subscriptions = new Subscription();

  settingsSideMenuItems = [
    {
      name: 'Status',
      link: [`/${DASHBOARD_ROUTES.SETTINGS}/${SETTINGS_ROUTES.STATUS}`],
    },
    {
      name: 'Requests',
      link: [`/${DASHBOARD_ROUTES.SETTINGS}/${SETTINGS_ROUTES.REQUESTS}`],
    },
    {
      name: 'Researchers',
      link: [`/${DASHBOARD_ROUTES.SETTINGS}/${SETTINGS_ROUTES.RESEARCHERS}`],
    },
    {
      name: 'Participants',
      link: [`/${DASHBOARD_ROUTES.SETTINGS}/${SETTINGS_ROUTES.PARTICIPANTS}`],
    },
    {
      name: 'Release Notes',
      link: [`/${DASHBOARD_ROUTES.SETTINGS}/${SETTINGS_ROUTES.RELEASE_NOTES}`],
    },
  ];

  createMenuItems = [
    { name: 'Studies', link: [`/${DASHBOARD_ROUTES.STUDIES}`] },
    {
      name: 'Study Details',
      link: [`/${DASHBOARD_ROUTES.STUDIES}/${STUDIES_ROUTES.CREATE_STUDY}`],
    },
  ];

  tiimMenuItems = [
    { name: 'Studies', link: [`/${DASHBOARD_ROUTES.STUDIES}`], suffix: '' },
    { name: 'Study Home', link: [], suffix: STUDIES_ROUTES.HOME },
    {
      name: 'Study Details',
      link: [],
      suffix: STUDIES_ROUTES.DETAILS,
      section: PRIVILEGE_SECTIONS.STUDY_DETAILS,
    },
    {
      name: 'Subscription',
      link: [],
      suffix: STUDIES_ROUTES.SUBSCRIPTION,
      error_msg: 'Translations are missing for certain languages',
    },
    {
      name: 'Interventions',
      link: [],
      suffix: STUDIES_ROUTES.INTERVENTIONS,
      section: PRIVILEGE_SECTIONS.INTERVENTIONS,
    },
    {
      name: 'Modules',
      link: [],
      suffix: STUDIES_ROUTES.MODULES,
      section: PRIVILEGE_SECTIONS.MODULES,
    },
    {
      name: 'Calculated Variables',
      link: [],
      suffix: STUDIES_ROUTES.CALCULATED_VARIABLES,
    },
    {
      name: 'E-Mails',
      link: [],
      suffix: STUDIES_ROUTES.EMAILS,
    },
    {
      name: 'Participants',
      link: [],
      suffix: STUDIES_ROUTES.TIIM_PARTICIPANTS,
      section: PRIVILEGE_SECTIONS.PARTICIPANTS,
    },
    {
      name: 'Metrics',
      link: [],
      suffix: STUDIES_ROUTES.METRICS,
      section: PRIVILEGE_SECTIONS.METRICS,
    },
    {
      name: 'Researchers',
      link: [],
      suffix: STUDIES_ROUTES.RESEARCHERS,
      section: PRIVILEGE_SECTIONS.RESEARCHERS,
    },
  ];

  ccMenuItems = [
    { name: 'Studies', link: [`/${DASHBOARD_ROUTES.STUDIES}`], suffix: '' },
    { name: 'Study Home', link: [], suffix: STUDIES_ROUTES.HOME },
    {
      name: 'Study Details',
      link: [],
      suffix: STUDIES_ROUTES.DETAILS,
      section: PRIVILEGE_SECTIONS.STUDY_DETAILS,
    },
    {
      name: 'Informative pages',
      link: [],
      suffix: STUDIES_ROUTES.INFORMATIVE_PAGES,
    },
    {
      name: 'Interventions',
      link: [],
      suffix: STUDIES_ROUTES.INTERVENTIONS,
      section: PRIVILEGE_SECTIONS.INTERVENTIONS,
    },
    {
      name: 'Modules',
      link: [],
      suffix: STUDIES_ROUTES.MODULES,
      section: PRIVILEGE_SECTIONS.MODULES,
    },
    {
      name: 'Calculated Variables',
      link: [],
      suffix: STUDIES_ROUTES.CALCULATED_VARIABLES,
    },
    {
      name: 'E-Mails',
      link: [],
      suffix: STUDIES_ROUTES.EMAILS,
    },
    {
      name: 'Participants',
      link: [],
      suffix: STUDIES_ROUTES.CC_PARTICIPANTS,
      section: PRIVILEGE_SECTIONS.PARTICIPANTS,
    },
    {
      name: 'Metrics',
      link: [],
      suffix: STUDIES_ROUTES.METRICS,
      section: PRIVILEGE_SECTIONS.METRICS,
    },
    {
      name: 'Researchers',
      link: [],
      suffix: STUDIES_ROUTES.RESEARCHERS,
      section: PRIVILEGE_SECTIONS.RESEARCHERS,
    },
    { name: 'Templates', link: [], suffix: STUDIES_ROUTES.TEMPLATES },
  ];

  constructor(
    private store: Store<AppState>,
    public privilegeService: PrivilegeService,
    private subscriptionService: SubscriptionService
  ) {}

  ngOnInit(): void {
    this.sidenavType$ = this.store.select(selectSideNavType);
    this.loadingSelectedStudy$ = this.store.select(selectIsLoadingSelectedStudy);
    this.selectedStudyLoaded$ = this.store.select(selectSelectedStudyLoaded);
    this.supportedLanguages$ = this.store.select(selectSupportedLanguages);

    this.selectedStudySubscription = this.store.select(selectSelectedStudy).subscribe((selectedStudy) => {
      this.selectedStudy = selectedStudy;

      if (this.selectedStudy?.id) {
        this.subscriptionService.getSubscription(this.selectedStudy?.id).subscribe((subscription) => {
          this.subscriptionTranslations = subscription?.translations;
        });
      }
      this.buildNavLinks(this.tiimMenuItems);
      this.buildNavLinks(this.ccMenuItems);
    });

    this.subscriptions.add(
      this.store
        .select(selectSupportedLanguages)
        .subscribe((supportedLanguages) => (this.supportedLanguages = supportedLanguages))
    );
  }

  buildNavLinks(items: { name: string; link: string[]; suffix: string }[]): void {
    items.slice(1).forEach((item) => {
      item.link = [`/${DASHBOARD_ROUTES.STUDIES}/${this.selectedStudy?.id}/${item.suffix}`];
    });
  }

  checkIfCompleteSubscriptionTranslations() {
    var allTranslations = true;
    this.supportedLanguages.forEach((lang) => {
      const filteredTranslations = this.subscriptionTranslations?.filter(
        (translation) => translation.language === lang.code
      );

      if (filteredTranslations && filteredTranslations.length == 0) {
        allTranslations = false;
      }
    });

    return allTranslations;
  }

  ngOnDestroy(): void {
    this.selectedStudySubscription.unsubscribe();
  }
}
